import { useState, useEffect, useCallback } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

// Clave compartida con WhatsappPage: si cambia la clave VAPID del servidor,
// se resuscribe automáticamente.
const PUSH_VAPID_KEY_STORAGE = 'waPushVapidKey';

const urlBase64ToUint8Array = (base64String = '') => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const pushSupported = () =>
  typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

/**
 * Hook reutilizable de notificaciones push del staff.
 *
 * Registra la suscripción del navegador para CUALQUIER usuario autenticado,
 * independientemente de su módulo. Antes el alta de push solo ocurría al abrir
 * la página de WhatsApp, por lo que el personal solo de soporte nunca se
 * suscribía y no recibía las notificaciones de tickets. Al usar este hook desde
 * la barra lateral (siempre montada), todo el staff puede activarlas.
 */
export default function usePushSubscription({ auto = true } = {}) {
  const [permission, setPermission] = useState(() => (pushSupported() ? Notification.permission : 'unsupported'));
  const [ready, setReady] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const sync = useCallback(async ({ requestPermission = false } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (!pushSupported()) {
      setPermission('unsupported');
      setReady(false);
      return false;
    }
    let perm = Notification.permission;
    if (requestPermission && perm !== 'granted') {
      perm = await Notification.requestPermission();
    }
    setPermission(perm);
    if (perm !== 'granted') {
      setReady(false);
      localStorage.removeItem(PUSH_VAPID_KEY_STORAGE);
      return false;
    }

    setSubscribing(true);
    try {
      const keyRes = await apiFetch('/api/push/public-key', { headers: { Authorization: `Bearer ${token}` } });
      if (keyRes.status === 401) return false;
      const keyData = await parseJsonSafe(keyRes);
      if (!keyRes.ok || !keyData?.publicKey) {
        throw new Error((keyData && keyData.message) || 'No se pudo obtener la clave de notificaciones');
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      const storedVapidKey = localStorage.getItem(PUSH_VAPID_KEY_STORAGE) || '';
      if (subscription && (!storedVapidKey || storedVapidKey !== keyData.publicKey)) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
      }
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
        });
      }

      const subRes = await apiFetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON ? subscription.toJSON() : subscription })
      });
      if (subRes.status === 401) return false;
      const subData = await parseJsonSafe(subRes);
      if (!subRes.ok) throw new Error((subData && subData.message) || 'No se pudo registrar la suscripción push');
      localStorage.setItem(PUSH_VAPID_KEY_STORAGE, keyData.publicKey);
      setReady(true);
      return true;
    } catch (err) {
      console.error('Error activando push', err);
      setReady(false);
      return false;
    } finally {
      setSubscribing(false);
    }
  }, []);

  useEffect(() => {
    if (!auto) return;
    if (!pushSupported()) {
      setPermission('unsupported');
      setReady(false);
      return;
    }
    setPermission(Notification.permission);
    // Solo resuscribe silenciosamente si el usuario ya concedió permiso alguna vez.
    if (Notification.permission === 'granted') {
      sync({ requestPermission: false });
    }
  }, [auto, sync]);

  return { permission, ready, subscribing, sync, supported: pushSupported() };
}
